/**
 * EditSensorDialog.jsx - Dialog pour modifier un capteur existant
 *
 * Permet de modifier:
 * - Nom du capteur
 * - Type de capteur
 * - Secteur d'appartenance
 * - Localisation GPS (position sur la carte)
 * - Métadonnées
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Grid,
  IconButton,
  CircularProgress,
  Alert
} from '@mui/material';
import { Close, MyLocation, Edit as EditIcon } from '@mui/icons-material';
import FarmMapService from './FarmMapService';

/**
 * Dialog pour éditer un capteur
 */
const EditSensorDialog = ({
  open,
  onClose,
  farmId,
  sensor,
  sectors = [],
  onSensorUpdated
}) => {
  // État du formulaire
  const [formData, setFormData] = useState({
    sensor_name: '',
    sensor_type: '',
    sector_id: '',
    gps_latitude: '',
    gps_longitude: ''
  });

  // État de l'interface
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isGettingLocation, setIsGettingLocation] = useState(false);

  // Pré-remplir le formulaire avec les données du capteur
  useEffect(() => {
    if (open && sensor) {
      setFormData({
        sensor_name: sensor.sensor_name || '',
        sensor_type: sensor.sensor_type || '',
        sector_id: sensor.sector_id || '',
        gps_latitude: sensor.gps_latitude || '',
        gps_longitude: sensor.gps_longitude || ''
      });
      setError('');
    }
  }, [open, sensor]);

  /**
   * Gérer les changements de champ
   */
  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    if (error) setError('');
  };

  /**
   * Obtenir la position GPS de l'utilisateur
   */
  const handleGetLocation = async () => {
    if (!navigator.geolocation) {
      setError('Géolocalisation non supportée par ce navigateur');
      return;
    }

    setIsGettingLocation(true);
    setError('');

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        });
      });

      setFormData(prev => ({
        ...prev,
        gps_latitude: position.coords.latitude.toFixed(6),
        gps_longitude: position.coords.longitude.toFixed(6)
      }));
    } catch (err) {
      setError(`Impossible d'obtenir la position: ${err.message}`);
    } finally {
      setIsGettingLocation(false);
    }
  };

  /**
   * Valider le formulaire
   */
  const validateForm = () => {
    // Validation coordonnées si fournies
    if (formData.gps_latitude || formData.gps_longitude) {
      const lat = parseFloat(formData.gps_latitude);
      const lng = parseFloat(formData.gps_longitude);
      if (isNaN(lat) || lat < -90 || lat > 90) {
        setError('Latitude invalide (-90 à 90)');
        return false;
      }
      if (isNaN(lng) || lng < -180 || lng > 180) {
        setError('Longitude invalide (-180 à 180)');
        return false;
      }
    }

    return true;
  };

  /**
   * Soumettre le formulaire
   */
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      // Mise à jour du capteur
      const updateData = {
        farmId,
        sensorName: formData.sensor_name.trim() || undefined,
        sensorType: formData.sensor_type || undefined,
        sectorId: formData.sector_id || undefined,
        gpsLatitude: formData.gps_latitude ? parseFloat(formData.gps_latitude) : undefined,
        gpsLongitude: formData.gps_longitude ? parseFloat(formData.gps_longitude) : undefined,
        metadata: {
          updated_at: new Date().toISOString(),
          updated_via: 'map_interface'
        }
      };

      const updatedSensor = await FarmMapService.updateSensor(sensor.source_id, updateData);

      // Notifier le parent
      if (onSensorUpdated) {
        onSensorUpdated(updatedSensor);
      }

      // Fermer le dialog
      onClose();

    } catch (err) {
      console.error('Erreur modification capteur:', err);
      setError(err.message || 'Erreur lors de la modification du capteur');
    } finally {
      setLoading(false);
    }
  };

  if (!sensor) return null;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: 3000 }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Modifier le capteur</Typography>
          <IconButton onClick={onClose} disabled={loading} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          {/* Numéro de série (lecture seule) */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Numéro de série"
              value={sensor.source_id}
              disabled
              helperText="L'identifiant ne peut pas être modifié"
            />
          </Grid>

          {/* Nom du capteur */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Nom (optionnel)"
              value={formData.sensor_name}
              onChange={handleChange('sensor_name')}
              placeholder="Ex: Capteur température serre 1"
              disabled={loading}
            />
          </Grid>

          {/* Type de capteur */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Type de capteur (optionnel)"
              value={formData.sensor_type}
              onChange={handleChange('sensor_type')}
              placeholder="Ex: temperature_humidity"
              disabled={loading}
              helperText="Type de mesure (température, humidité, etc.)"
            />
          </Grid>

          {/* Secteur */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="sector-select-label">Secteur (optionnel)</InputLabel>
              <Select
                labelId="sector-select-label"
                value={String(formData.sector_id)}
                label="Secteur (optionnel)"
                onChange={handleChange('sector_id')}
                disabled={loading || sectors.length === 0}
              >
                <MenuItem value="">
                  <em>Aucun secteur</em>
                </MenuItem>
                {sectors.map((sector) => (
                  <MenuItem key={sector.sector_id} value={String(sector.sector_id)}>
                    {sector.name || `Secteur ${sector.sector_id}`}
                    {sector.compartment_name && ` - ${sector.compartment_name}`}
                  </MenuItem>
                ))}
              </Select>
              {sectors.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Aucun secteur disponible
                </Typography>
              )}
            </FormControl>
          </Grid>

          {/* Coordonnées GPS */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Localisation GPS (optionnel)
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<MyLocation />}
                onClick={handleGetLocation}
                disabled={loading || isGettingLocation}
              >
                {isGettingLocation ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    Obtention...
                  </>
                ) : (
                  'Utiliser ma position'
                )}
              </Button>
              <Typography variant="caption" color="text.secondary">
                ou saisir manuellement
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Latitude"
              value={formData.gps_latitude}
              onChange={handleChange('gps_latitude')}
              placeholder="Ex: 35.088056"
              disabled={loading}
              type="number"
              inputProps={{ step: "0.000001" }}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Longitude"
              value={formData.gps_longitude}
              onChange={handleChange('gps_longitude')}
              placeholder="Ex: -6.108250"
              disabled={loading}
              type="number"
              inputProps={{ step: "0.000001" }}
            />
          </Grid>

          {/* Aide */}
          <Grid item xs={12}>
            <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                💡 <strong>Conseil:</strong> Vous pouvez également déplacer le capteur directement sur la carte en mode édition.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Annuler
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <EditIcon />}
        >
          {loading ? 'Modification...' : 'Enregistrer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditSensorDialog;
